/**
 * ACF Session Detail Page
 *
 * Chat interface for an AI coding session
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Settings, Loader2, AlertCircle } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@forgenetwork/ui';
import { ChatWindow } from '@/components/acf';
import {
  useACFSession,
  useUpdateACFSession,
  useDeleteACFSession,
} from '@/hooks/useACF';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/_authenticated/repositories/$repoId/acf/$sessionId')({
  component: ACFSessionDetailPage,
});

function ACFSessionDetailPage(): React.JSX.Element {
  const { repoId, sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  // Fetch session
  const { data: session, isLoading, error } = useACFSession(sessionId);

  // Mutations
  const updateSession = useUpdateACFSession();
  const deleteSession = useDeleteACFSession();

  // Local state for settings form
  const [editedTitle, setEditedTitle] = React.useState('');
  const [editedModel, setEditedModel] = React.useState('');

  // Sync form state when session loads
  React.useEffect(() => {
    if (session) {
      setEditedTitle(session.title || '');
      setEditedModel(session.settings.model || 'claude-3-sonnet');
    }
  }, [session]);

  const handleBack = (): void => {
    navigate({
      to: '/repositories/$repoId/acf',
      params: { repoId },
    });
  };

  const handleSaveSettings = async (): Promise<void> => {
    try {
      await updateSession.mutateAsync({
        sessionId,
        updates: {
          title: editedTitle || undefined,
          settings: {
            model: editedModel || undefined,
          },
        },
      });

      toast({
        title: 'Settings Saved',
        description: 'Session settings have been updated.',
      });

      setSettingsOpen(false);
    } catch (err) {
      toast({
        title: 'Update Failed',
        description: err instanceof Error ? err.message : 'Failed to update settings',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!session) return;

    try {
      await deleteSession.mutateAsync({
        sessionId,
        workspaceId: session.workspaceId,
      });

      toast({
        title: 'Session Deleted',
        description: 'The session has been deleted.',
      });

      setDeleteDialogOpen(false);

      // Navigate back to sessions list
      navigate({
        to: '/repositories/$repoId/acf',
        params: { repoId },
      });
    } catch (err) {
      toast({
        title: 'Delete Failed',
        description: err instanceof Error ? err.message : 'Failed to delete session',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[600px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-destructive">
                {error instanceof Error ? error.message : 'Session not found'}
              </p>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sessions
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {session.title || 'AI Coding Session'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {session.settings.model || 'claude-3-sonnet'}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>

        {/* Chat Window */}
        <div className="h-[calc(100vh-200px)] min-h-[500px]">
          <ChatWindow
            sessionId={sessionId}
            onSettingsClick={() => setSettingsOpen(true)}
            onDelete={() => setDeleteDialogOpen(true)}
          />
        </div>

        {/* Settings Sheet */}
        <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Session Settings</SheetTitle>
              <SheetDescription>
                Configure your AI coding session settings.
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-6 py-6">
              <div className="space-y-2">
                <Label htmlFor="session-title">Session Title</Label>
                <Input
                  id="session-title"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  placeholder="Enter session title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">AI Model</Label>
                <Select value={editedModel} onValueChange={setEditedModel}>
                  <SelectTrigger id="model">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude-3-haiku">Claude 3 Haiku (Fast)</SelectItem>
                    <SelectItem value="claude-3-sonnet">Claude 3 Sonnet (Balanced)</SelectItem>
                    <SelectItem value="claude-3-opus">Claude 3 Opus (Most Capable)</SelectItem>
                    <SelectItem value="gpt-4">GPT-4</SelectItem>
                    <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose the AI model for this session.
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  className="flex-1"
                  onClick={handleSaveSettings}
                  disabled={updateSession.isPending}
                >
                  {updateSession.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>

              <div className="border-t pt-6 mt-6">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => {
                    setSettingsOpen(false);
                    setDeleteDialogOpen(true);
                  }}
                >
                  Delete Session
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Session</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this session? This will permanently
                remove the conversation history. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteSession.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
